import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Receipt, Undo2, Mail } from 'lucide-react';
import { formatDateNL } from '../../lib/billingDates';

interface CancellationRow {
  id: string;
  user_id: string;
  cancelled_at: string;
  effective_end_date: string;
  reasons: string[];
  reason_other: string | null;
  reversed_at: string | null;
  reversed_by: string | null;
  notification_sent: boolean | null;
  user: {
    email: string;
    first_name: string;
    company_name: string;
  } | null;
}

const REASON_LABELS: Record<string, string> = {
  too_expensive: 'Te duur',
  missing_features: 'Niet de juiste functies',
  wrong_fit: 'Werkt niet voor mijn type bedrijf',
  paused_business: 'Tijdelijk gestopt',
  switched_tool: 'Overgestapt',
  other: 'Anders',
};

type FilterType = 'active' | 'reversed' | 'all';

export function AdminCancellations() {
  const [rows, setRows] = useState<CancellationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('active');
  const [reversingId, setReversingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, [filter]);

  const load = async () => {
    setLoading(true);
    setError(null);
    let query = supabase
      .from('subscription_cancellations')
      .select(`
        id, user_id, cancelled_at, effective_end_date, reasons, reason_other,
        reversed_at, reversed_by, notification_sent,
        user:users!subscription_cancellations_user_id_fkey(email, first_name, company_name)
      `)
      .order('cancelled_at', { ascending: false });

    if (filter === 'active') query = query.is('reversed_at', null);
    if (filter === 'reversed') query = query.not('reversed_at', 'is', null);

    const { data, error } = await query;
    if (error) {
      setError(error.message);
      setRows([]);
    } else {
      setRows((data ?? []) as unknown as CancellationRow[]);
    }
    setLoading(false);
  };

  const handleReverse = async (id: string) => {
    if (!confirm('Weet je zeker dat je deze opzegging ongedaan wilt maken? De klant ontvangt een bevestiging per e-mail.')) {
      return;
    }
    setReversingId(id);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Niet ingelogd');
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reverse-cancellation`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ cancellation_id: id }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Heractiveren mislukt');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setReversingId(null);
    }
  };

  const filterButton = (key: FilterType, label: string) => (
    <button
      onClick={() => setFilter(key)}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
        filter === key
          ? 'bg-[#FF6B35] text-white'
          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
      }`}
    >
      {label}
    </button>
  );

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#231F20] flex items-center gap-2">
              <Receipt className="w-7 h-7" />
              Opzeggingen
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Klanten die hun abonnement hebben opgezegd. Stop incasso handmatig in Plug&Pay.
            </p>
          </div>
          <div className="flex gap-2">
            {filterButton('active', 'Actief')}
            {filterButton('reversed', 'Heractiveerd')}
            {filterButton('all', 'Alles')}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-500">
            Geen opzeggingen voor dit filter.
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left">Klant</th>
                  <th className="px-4 py-3 text-left">Opgezegd op</th>
                  <th className="px-4 py-3 text-left">Einddatum</th>
                  <th className="px-4 py-3 text-left">Redenen</th>
                  <th className="px-4 py-3 text-left">Mail</th>
                  <th className="px-4 py-3 text-right">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {rows.map(row => (
                  <tr key={row.id} className={row.reversed_at ? 'bg-gray-50' : ''}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#231F20]">
                        {row.user?.first_name ?? '?'} ({row.user?.company_name ?? '—'})
                      </div>
                      <div className="text-gray-500 text-xs">{row.user?.email ?? '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {new Date(row.cancelled_at).toLocaleString('nl-NL', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{formatDateNL(row.effective_end_date)}</td>
                    <td className="px-4 py-3 text-gray-700">
                      <div className="flex flex-wrap gap-1">
                        {row.reasons.map(r => (
                          <span key={r} className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                            {REASON_LABELS[r] ?? r}
                          </span>
                        ))}
                      </div>
                      {row.reason_other && (
                        <div className="text-gray-500 text-xs mt-1 italic" title={row.reason_other}>
                          "{row.reason_other.slice(0, 80)}{row.reason_other.length > 80 ? '...' : ''}"
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Mail className={`w-4 h-4 ${row.notification_sent ? 'text-green-600' : 'text-amber-500'}`} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.reversed_at ? (
                        <span className="text-xs text-gray-500">
                          Heractiveerd {new Date(row.reversed_at).toLocaleDateString('nl-NL')}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleReverse(row.id)}
                          disabled={reversingId === row.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                          {reversingId === row.id ? 'Bezig...' : 'Heractiveren'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
